/// <amd-module name="Build/Queue-Panel/ContainerResourcePanel" />
import * as React from "react";

import { ContainerResourceData, ContainerResourceRunData } from "Build/Client/Contracts/DataProvider";
import * as Resources from "Build/Queue-Panel/Resources";

import { QueueBuildStore } from "Build/Queue-Panel/Stores/QueueBuild";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Button } from "VSSUI/Button";
import { ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import * as UIResources from "VSSUI/Resources.Input";
import { Spinner } from "VSSUI/Spinner";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { ColumnSelect, ITableColumn, ITableRow, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import * as TooltipEx from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface IContainerResourcePanelProps {
    pageContext: IVssPageContext;
    store: QueueBuildStore;
    resource: ContainerResourceData;
    onUpdate(resource: ContainerResourceData): void;
    onDismiss(): void;
}

interface IContainerResourcePanelState extends ContainerResourceData {
    searchText: string;
    useSelectedImageDisabled: boolean;
}

interface ITableItem extends ContainerResourceRunData {
}

export class ContainerResourcePanel extends React.Component<IContainerResourcePanelProps, IContainerResourcePanelState> {

    private _isLoading: IObservableValue<boolean>;
    private _selection: ListSelection = new ListSelection({ selectOnFocus: false, multiSelect: false});
    private _selectedRun: string;
    private _runs: IObservableArray<ContainerResourceRunData>;
    private _filteredRuns: IObservableArray<ContainerResourceRunData>;

    constructor(props: IContainerResourcePanelProps) {
        super(props);
        this._isLoading = new ObservableValue(false);
        this._runs =  new ObservableArray([]);
        this._filteredRuns =  new ObservableArray([]);
        const state = { ...props.resource, searchText: "", useSelectedImageDisabled: false };
        this.state = state;
    }

    public componentDidMount() {
        this._initializeResourceVersions();
    }
    
    public render(): JSX.Element {
        return (
            <Panel
                backButtonProps={{ onClick: this.props.onDismiss }}
                contentClassName="queue-panel-child no-padding"
                description={Resources.PipelineResourcePanelDescription}
                escDismiss={true}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: this.props.resource.name }}
                className="pipeline-resource-panel"
                footerButtonProps={[
                    {
                        text: Resources.CancelButtonText,
                        onClick: this.props.onDismiss
                    },
                    {
                        disabled: this.state.useSelectedImageDisabled,
                        text: Resources.ContainerResourceUpdateButtonText,
                        primary: true,
                        onClick: this._update
                    }]}>
                <div className="margin-vertical-8 flex-grow">
                    {this._getSearchBar()}
                    {this._buildVersionsTableComponent()}
                </div>
            </Panel>
        );
    }

    public componentWillUnmount() {
        this.props.store.containerResourceRuns && this.props.store.containerResourceRuns.unsubscribe(this._updateResourceRuns);
    }

    private async _initializeResourceVersions() {
        if (!this.props.store.getContainerResourceRuns(this.state.alias)) {
            this._isLoading.value = true;
            await this.props.store.fetchContainerResourceRuns(this.state.alias, this.state.connectionId, this.state.containerType, this.state.properties);
            this._isLoading.value = false;
        }
        this._updateResourceRuns();
        this.props.store.containerResourceRuns && this.props.store.containerResourceRuns.subscribe(this._updateResourceRuns);
    }

    private _buildVersionsTableComponent(): React.ReactNode {
        return (
                <div className={ "flex flex-grow flex-column"}>
                    <Observer loading={this._isLoading}  versions={this._filteredRuns}>
                        {(props: { loading: boolean, versions: ContainerResourceRunData[] }) => props.loading
                            ? (<div><Spinner className="queue-build-spinner" /></div>)
                            : (
                                <Observer versions={this._filteredRuns}>
                                    {(props: { versions: ContainerResourceRunData[] }) =>
                                        {
                                            if (props.versions.length > 0) {
                                                return (<Table
                                                            selection={this._selection} 
                                                            onSelect={(event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<ContainerResourceRunData>): void => {
                                                                let disableSelectedButton: boolean = false;
                                                                if (this._selection.selectedCount == 0) {
                                                                    disableSelectedButton = true;
                                                                }
                                                                this.setState({useSelectedImageDisabled: disableSelectedButton});
                                                                this._selectedRun = tableRow.data.imageId;
                                                            }}
                                                            columns={this._tableColumns}
                                                            itemProvider={this._getItemProvider(props.versions)} />);
                                            } else {
                                                const message: string = this.state.searchText ? format(Resources.PipelineResourceNoSearchResultsText, this.state.searchText) :Resources.ContainerResourceNoRunsText;
                                                return (<div className="margin-vertical-8 padding-horizontal-20">
                                                            <MessageCard severity={MessageCardSeverity.Info}>{message}</MessageCard>
                                                        </div>);
                                            }
                                        }
                                    }
                                </Observer>
                            )}
                    </Observer>
                </div>
        );
    }

    private _renderRunCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        const runLine = (<TooltipEx.Tooltip text={tableItem.imageId} overflowOnly={true}>
                            <span className="text-ellipsis primary-text">#{tableItem.imageId}</span>
                        </TooltipEx.Tooltip>);

        return ( 
            <TwoLineTableCell 
                columnIndex={columnIndex}  
                tableColumn={tableColumn} 
                key={"col-" + columnIndex}
                iconProps={{ render: () => {
                    return (<Status
                    {... this._getStatusPropsForBuildResult()}
                    key="result"
                    size={StatusSize.l}
                    className="flex-self-center margin-right-8"
                />);
                }}}
                line1={runLine} 
                line2={<span></span>}/>);
        
    }

    private _getSearchBar(): JSX.Element {
        return (
            <TextField
                placeholder={Resources.PipelineResourceSearchBarText}
                className="margin-horizontal-20 margin-bottom-8"
                onChange={this._onChangeSearchText}
                prefixIconProps={{ iconName: "Search" }}
                suffixIconProps={
                    this.state.searchText
                    ?   {
                            render: className => {
                                return (
                                    <Button
                                        ariaLabel={UIResources.ClearFilter}
                                        className={css(className, "bolt-text-filterbaritem-clear")}
                                        iconProps={{ iconName: "Cancel"}}
                                        onClick={this._onClickClearButton}
                                    />
                                );
                            }
                        }
                    : undefined
                }
                value={this.state.searchText}
            />
        );
    }

    private _getItemProvider(runs: ContainerResourceRunData[]): ArrayItemProvider<ITableItem> {
        const rows: ITableItem[] = runs.map<ITableItem>(run => {
            return run as ITableItem;
        });
        return new ArrayItemProvider<ITableItem>(rows);
    }

    private _update = () => {
        const resource = { ...this.state };
        resource.version = this._selectedRun;
        this.props.onUpdate(resource);
        this.props.onDismiss();
    }

    private _onChangeSearchText = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
        this._searchContainerRuns(newValue);
    }

    private _onClickClearButton = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this._filteredRuns.value = this._runs.value;
        this._updateSelection(this._filteredRuns.value);
        this.setState({
            searchText: ""
        });
    };

    private _searchContainerRuns = (searchValue: string): void => {
        const searchValueLower = searchValue.toLowerCase();
        const filteredRuns = this._runs.value.filter(r => r.imageId.toLowerCase().indexOf(searchValueLower) > -1);
        this._filteredRuns.value = filteredRuns;
        this._updateSelection(filteredRuns);
        this.setState({
            searchText: searchValue
        });
    }

    private _updateSelection = (runs: ContainerResourceRunData[]): void => {
        let isRunSelected = false;
        if (this.state.version) {
            runs.some((run, index) => {
                if (equals(run.imageId, this.state.version)) {
                    this._selection.select(index);
                    this.setState({
                        useSelectedImageDisabled: false
                    });
                    isRunSelected = true;    
                    return true;
                }
            });
            
            //clear the previous selection as none of the image match the version
            if(!isRunSelected) {
                this._selection.clear();
            }
        } 
        else if(!this.state.searchText) {
            //selecting the latest image as default in case of no searchText and no version selected
            if (runs.length > 0) {
                this._selection.select(0);
                this._selectedRun = runs[0].imageId
                this.setState({
                    version: runs[0].imageId,
                    useSelectedImageDisabled: false
                });
            }
        }
    }

    private _updateResourceRuns = (): void => {
        const runs: ContainerResourceRunData[] = [];
        if (this.props.store.containerResourceRuns) {
            const containerRuns: ContainerResourceRunData[] | undefined = this.props.store.containerResourceRuns.get(this.state.alias);
            if (containerRuns && containerRuns.length > 0){
                runs.push(...containerRuns);
            }
        }

        this._runs.value = runs;
        if (this.state.searchText) {
            this._searchContainerRuns(this.state.searchText);
        }
        else {
            this._filteredRuns.value = runs;
            this._updateSelection(runs);
        }
    }

    private _getStatusPropsForBuildResult = (): IStatusProps => {
        return Statuses.Success;
    }

    private _tableColumns: ITableColumn<ITableItem>[] = [
        new ColumnSelect(), {
            id: "pipeline-run",
            width: 404,
            name: "Pipeline run",
            renderCell: this._renderRunCell
        }
    ];
}
